/**
 * TradeScoreBadge · Iter 160 · all 9 legal layers in one component
 *
 *   L1 — Renamed to "Verqo Trade Score" (no more "Credit Score")
 *   L2 — Disclaimer tooltip on every render + footnote chip
 *   L4 — Renders "Score withheld" pill when buyer opted out
 *   L5 — BETA chip baked in
 *   L7 — Links to /legal/trade-score for ToS disclosure
 *   L9 — Descriptive only (band letter + score; NO probability claims)
 *
 * Drop this anywhere a score was previously rendered. Pass either a
 * pre-fetched envelope (`data`) OR a `userId` to fetch it on mount.
 */
import { useEffect, useState } from "react";
import { Info, ShieldAlert, ExternalLink, EyeOff, Layers } from "lucide-react";
import api from "../lib/api";

const BAND_TONE = {
  AA: "emerald", A: "emerald", BB: "lime",   B: "amber",
  CC: "amber",   C: "orange",  DD: "rose",   D: "rose",
};
const MATURITY = {
  thin_file:    { label: "Thin file",    tone: "bg-amber-50 text-amber-800 border-amber-200" },
  establishing: { label: "Establishing", tone: "bg-sky-50 text-sky-700 border-sky-200" },
  established:  { label: "Established",  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
function tone(t) {
  return ({
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    lime:    "bg-lime-50 text-lime-800 border-lime-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    orange:  "bg-orange-50 text-orange-800 border-orange-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
  })[t] || "bg-foreground/5 text-foreground/70 border-border";
}

export default function TradeScoreBadge({ userId, data: pre, size = "md", showLink = true }) {
  const [data, setData] = useState(pre || null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (pre || !userId) return;
    api.get(`/trade-score/${userId}`).then(r => setData(r.data)).catch(() => {});
  }, [userId, pre]);

  if (!data) return <span className="mono text-[10px] text-foreground/40" data-testid="trade-score-loading">…</span>;

  const sml = size === "sm";
  const padX = sml ? "px-2 py-0.5" : "px-2.5 py-1";

  // L4 — withheld
  if (data.withheld_by_buyer) {
    return (
      <span
        className={`inline-flex items-center gap-1 ${padX} rounded-full border border-border bg-foreground/[0.04] text-foreground/60 text-[11px] mono`}
        data-testid="trade-score-withheld"
        title="The buyer has chosen to hide their Verqo Trade Score from sellers. Reach out directly for trade references."
      >
        <EyeOff className="w-3 h-3"/>Score withheld
      </span>
    );
  }

  const bandClass = tone(BAND_TONE[data.band] || "amber");

  return (
    <span
      className="relative inline-flex items-center gap-1.5"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="trade-score-badge"
    >
      <span className={`inline-flex items-center gap-1 ${padX} rounded-full border ${bandClass} text-[11.5px] mono`}>
        <span className="font-medium">{data.score}</span>
        <span className="opacity-70">·</span>
        <span>{data.band}</span>
      </span>
      <span
        className="inline-flex items-center gap-0.5 text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-foreground/20 bg-foreground/[0.04] text-foreground/60"
        data-testid="trade-score-beta"
      >
        BETA
      </span>
      {data.maturity && MATURITY[data.maturity] && (
        <span
          className={`inline-flex items-center gap-0.5 text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${MATURITY[data.maturity].tone}`}
          data-testid="trade-score-maturity"
          title={
            data.maturity === "thin_file"
              ? "Score is based entirely on external signals (Middesk, Plaid, bureau). Behavioral signals start accumulating after the buyer's first paid invoice on Verqo."
              : data.maturity === "establishing"
              ? `Score is blended (${data.data_source_mix?.external_pct ?? 0}% external · ${data.data_source_mix?.behavioral_pct ?? 0}% behavioral). Maturity grows with each paid invoice.`
              : `Score is mostly behavioral (${data.data_source_mix?.behavioral_pct ?? 0}%). External signals provide a safety net at ${data.data_source_mix?.external_pct ?? 0}%.`
          }
        >
          <Layers className="w-2.5 h-2.5"/>{MATURITY[data.maturity].label}
        </span>
      )}
      {data.disputed && (
        <span
          className="inline-flex items-center gap-0.5 text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800"
          data-testid="trade-score-disputed"
          title="Buyer has disputed this score. Verqo is reviewing."
        >
          <ShieldAlert className="w-2.5 h-2.5"/>Disputed
        </span>
      )}
      <Info className="w-3 h-3 text-foreground/45"/>
      {hover && (
        <span
          className="absolute z-50 left-0 top-full mt-1.5 w-[320px] rounded-xl border border-border bg-background shadow-lg p-3 text-[11.5px] text-foreground/75 leading-snug"
          data-testid="trade-score-tooltip"
        >
          <div className="font-medium text-foreground mb-1">Verqo Trade Score · BETA</div>
          <p className="mb-2">{data.disclaimer_full || data.disclaimer_short}</p>
          {data.data_source_mix && (
            <p className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 mb-1.5">
              Source mix: {data.data_source_mix.external_pct}% external · {data.data_source_mix.behavioral_pct}% behavioral
            </p>
          )}
          {data.confidence && (
            <p className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 mb-2">
              Confidence: {data.confidence} · Methodology {data.methodology}
            </p>
          )}
          {showLink && (
            <a
              href="/legal/trade-score"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-wider text-foreground hover:underline"
              data-testid="trade-score-methodology-link"
            >
              How is this computed? <ExternalLink className="w-2.5 h-2.5"/>
            </a>
          )}
        </span>
      )}
    </span>
  );
}
